import { Anchor } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { StandardCell } from "~/components/cells/StandardCell.tsx"
import {
  FindProductsProductStandardDto,
  ProductStandardsItemResponseDto,
} from "~/gen/api"
import { isCertificateVerified } from "~/pages/Brand/Products/List/Services/standardCompliance.tsx"

const columnHelper = createColumnHelper<ProductStandardsItemResponseDto>()

export const useProductsStandardsColumns = (
  standardHeaders: FindProductsProductStandardDto[],
  onStandardClick: (standard: FindProductsProductStandardDto) => void
) => {
  const navigate = useNavigate()
  const { t } = useTranslation(["products"])

  return useMemo<ColumnDef<ProductStandardsItemResponseDto, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <Anchor
            fz="xs"
            c="gray.9"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            <TextCell value={info.getValue()} />
          </Anchor>
        ),
      }),
      columnHelper.accessor("brandReference", {
        id: "brandReference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrderReference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) =>
          info.row.original.purchaseOrderReference ? (
            <TextCell value={info.row.original.purchaseOrderReference} />
          ) : (
            <Missing c="gray" />
          ),
      }),
      columnHelper.accessor("color", {
        header: t("lists.columns.color"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("collection", {
        header: t("lists.columns.collection"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      ...standardHeaders.map((standardHeader) =>
        columnHelper.accessor(
          (p) => {
            const standard = p.standards?.find(
              (standard) => standard.name == standardHeader.name
            )
            const totalCertificates = standard?.certificates.length
            const completedCertificates = standard?.certificates.filter(
              (certificate) => isCertificateVerified(certificate)
            ).length

            return p.standards && totalCertificates
              ? `${completedCertificates} / ${totalCertificates}`
              : ""
          },
          {
            id: standardHeader.name,
            header: standardHeader.name,
            enableSorting: false,
            cell: (info) => {
              const value = info.row.original.standards?.find(
                (standard) => standard.name == standardHeader.name
              )
              return <StandardCell value={value} onClick={onStandardClick} />
            },
          }
        )
      ),
    ],
    [t, standardHeaders, navigate, onStandardClick]
  )
}
